// CustomerSelector.tsx
// Customer Selection Component for Booking Flow

import React, { useState, useEffect, useRef } from 'react';
import { adminService } from '../../services/adminService';
import { serverSuchbegriff, gesamtzahlAusAntwort, kundenlisteHinweis, KUNDEN_SEITENGROESSE } from '../../utils/kundensuche';
import { CustomerDialog, Customer } from '../common/CustomerDialog';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/CustomerSelector.css';

interface CustomerSelectorProps {
  onCustomerSelect: (customer: Customer) => void;
  selectedCustomer: Customer | null;
  // T-141 B (v1.8.43): Vorschlag aus einer Terminanfrage (Suche vorbelegt, Dialog "Neuer Kunde" vorbelegt); ohne Prop wie bisher
  vorschlag?: {
    suchbegriff?: string;
    neu?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  };
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  onCustomerSelect,
  selectedCustomer,
  vorschlag
}) => {
  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(vorschlag?.suchbegriff || '');
  const [filterVip, setFilterVip] = useState(false);
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  // T-149 (v1.8.55): Gesamtzahl laut Server und Suchbegriff des letzten Abrufs (Hinweis, wenn nicht alles geladen ist)
  const [totalCount, setTotalCount] = useState(0);
  const [geladenFuer, setGeladenFuer] = useState<string | null>(null);
  const suchLauf = useRef(0);
  const ersteLadung = useRef(true);

  // Get user info from auth context
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'Staff';

  // Fetch customers on mount
  useEffect(() => {
    if (isAdmin) {
      // For admins, the customer list is loaded by the search effect below (T-149, v1.8.55)
    } else if (user) {
      // For regular customers, auto-select themselves
      const currentUserAsCustomer: Customer = {
        id: user.id,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email,
        phone: (user as any).phone || '',
        companyName: user.companyName || '',
        isVip: false,
        addresses: ((user as any).addresses || []).map((a: any) => ({
          street: a.street || '',
          city: a.city || '',
          postalCode: a.postalCode || '',
          country: a.country || 'Deutschland',
          type: a.addressType || 'billing',
          isDefault: a.isDefault || false
        }))
      };
      onCustomerSelect(currentUserAsCustomer);
    }
  }, [isAdmin, user]);

  // T-149 (v1.8.55): Suchbegriff ab 2 Zeichen auf dem Server (bisher nur die ersten 100 Kunden im Browser gefiltert),
  // Abruf leicht verzögert; veraltete Antworten werden verworfen
  useEffect(() => {
    if (!isAdmin) return;
    const suche = serverSuchbegriff(searchTerm);
    if (ersteLadung.current) {
      ersteLadung.current = false;
      fetchCustomers(suche, true);
      return;
    }
    const t = setTimeout(() => fetchCustomers(suche, false), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isAdmin]);

  const fetchCustomers = async (suche: string | null = null, mitLadeanzeige: boolean = true) => {
    const lauf = ++suchLauf.current;
    try {
      if (mitLadeanzeige) setLoading(true);
      setError(null);

      const response = await adminService.getCustomers({
        page: 1,
        pageSize: KUNDEN_SEITENGROESSE,
        ...(suche ? { search: suche } : {})
      });
      if (lauf !== suchLauf.current) return; // T-149: eine neuere Suche läuft bereits

      // Transform API response to Customer format
      const responseData: any = response;
      const customersData = responseData.data?.Customers || responseData.data?.customers || response.customers || [];

      const transformedCustomers: Customer[] = customersData.map((c: any) => ({
        id: (c.Id || c.id) || 0,
        firstName: c.FirstName || c.firstName || '',
        lastName: c.LastName || c.lastName || '',
        email: c.Email || c.email || '',
        phone: c.Phone || c.phone || '',
        landlinePhone: c.LandlinePhone || c.landlinePhone || '', // T-100 (v1.8.24)
        companyName: c.CompanyName || c.companyName || '',
        customerType: c.CustomerType || c.customerType || '', // T-099 (v1.8.24)
        isVip: c.IsVip || c.isVip || false,
        addresses: (c.Addresses || c.addresses || []).map((a: any) => ({
          street: a.Street || a.street || '',
          city: a.City || a.city || '',
          postalCode: a.PostalCode || a.postalCode || '',
          country: a.Country || a.country || 'Deutschland',
          type: (a.AddressType || a.addressType || 'billing') as any,
          isDefault: a.IsDefault || a.isDefault || false
        }))
      }));

      setCustomers(transformedCustomers);
      setTotalCount(gesamtzahlAusAntwort(responseData, transformedCustomers.length));
      setGeladenFuer(suche);
    } catch (error: any) {
      if (lauf !== suchLauf.current) return;
      console.error('Error fetching customers:', error);
      setError('Fehler beim Laden der Kundenliste. Bitte versuchen Sie es erneut.');
    } finally {
      if (lauf === suchLauf.current) setLoading(false);
    }
  };

  // Filter customers based on search and VIP filter
  // T-149 (v1.8.55, Review): hat der Server bereits gesucht, nicht noch einmal im Browser filtern (Trefferliste des Servers);
  // sonst Suche im Browser mit getrimmtem Begriff, auch Festnetz
  const suchtext = searchTerm.trim().toLowerCase();
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch =
      geladenFuer !== null ||
      suchtext === '' ||
      customer.firstName.toLowerCase().includes(suchtext) ||
      customer.lastName.toLowerCase().includes(suchtext) ||
      customer.email.toLowerCase().includes(suchtext) ||
      (customer.phone && customer.phone.includes(suchtext)) ||
      (customer.landlinePhone && customer.landlinePhone.includes(suchtext)) ||
      (customer.companyName && customer.companyName.toLowerCase().includes(suchtext));

    const matchesVip = !filterVip || customer.isVip;

    return matchesSearch && matchesVip;
  });

  // Handle new customer saved
  const handleNewCustomerSaved = (newCustomer: Customer) => {
    setCustomers([newCustomer, ...customers]);
    onCustomerSelect(newCustomer);
    setShowNewCustomerDialog(false);
  };

  return (
    <div className="customer-selector">
      {/* For non-admin users, show current user info */}
      {!isAdmin && selectedCustomer ? (
        <div className="current-user-info">
          <h3>Buchung für:</h3>
          <div className="customer-item selected">
            <div className="customer-info">
              <div className="customer-header">
                <h4>
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                </h4>
              </div>
              <p className="customer-email">
                <span className="icon">📧</span>
                {selectedCustomer.email}
              </p>
              {selectedCustomer.phone && (
                <p className="customer-phone">
                  <span className="icon">📞</span>
                  {selectedCustomer.phone}
                </p>
              )}
              {selectedCustomer.companyName && (
                <p className="customer-company">
                  <span className="icon">🏢</span>
                  <strong>{selectedCustomer.companyName}</strong>
                </p>
              )}
            </div>
          </div>
          <p className="info-text">Sie buchen diese Floßfahrt für sich selbst.</p>
        </div>
      ) : isAdmin ? (
        <>
          {/* Search & Filter Bar (Admin only) */}
          <div className="search-filter-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Suchen nach Name, Email, Telefon, Firma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <label className="vip-filter">
              <input
                type="checkbox"
                checked={filterVip}
                onChange={(e) => setFilterVip(e.target.checked)}
              />
              <span>Nur VIP-Kunden</span>
            </label>

            <button
              onClick={() => setShowNewCustomerDialog(true)}
              className="btn-new-customer"
            >
              <span className="btn-icon">➕</span>
              Neuer Kunde
            </button>
          </div>

          {/* T-149 (v1.8.55): Hinweis, wenn nicht alle Kunden bzw. Treffer geladen sind */}
          {!loading && !error && kundenlisteHinweis(customers.length, totalCount, geladenFuer) && (
            <p className="info-text" style={{ margin: '4px 0 8px', fontSize: '0.85rem', color: '#854d0e' }}>
              {kundenlisteHinweis(customers.length, totalCount, geladenFuer)}
            </p>
          )}

          {/* Customer List (Admin only) */}
          {loading ? (
        <div className="loading-state">
          <div className="spinner">⏳</div>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={() => fetchCustomers(serverSuchbegriff(searchTerm), true)} className="btn-retry">
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div className="customer-list">
          {filteredCustomers.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <p>Keine Kunden gefunden</p>
              <button
                onClick={() => setShowNewCustomerDialog(true)}
                className="btn-new-customer-large"
              >
                <span className="btn-icon">➕</span>
                Neuen Kunde anlegen
              </button>
            </div>
          ) : (
            filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className={`customer-item ${
                  selectedCustomer?.id === customer.id ? 'selected' : ''
                }`}
                onClick={() => onCustomerSelect(customer)}
              >
                <div className="customer-info">
                  <div className="customer-header">
                    <h4>
                      {customer.firstName} {customer.lastName}
                    </h4>
                    {customer.isVip && <span className="vip-badge">VIP</span>}
                  </div>
                  <p className="customer-email">
                    <span className="icon">📧</span>
                    {customer.email}
                  </p>
                  {customer.phone && (
                    <p className="customer-phone">
                      <span className="icon">📞</span>
                      {customer.phone}
                    </p>
                  )}
                  {customer.companyName && (
                    <p className="customer-company">
                      <span className="icon">🏢</span>
                      <strong>{customer.companyName}</strong>
                    </p>
                  )}
                </div>
                {selectedCustomer?.id === customer.id && (
                  <div className="selected-indicator">✓</div>
                )}
              </div>
            ))
          )}
        </div>
      )}

          {/* New Customer Dialog */}
          {showNewCustomerDialog && (
            <CustomerDialog
              onClose={() => setShowNewCustomerDialog(false)}
              onSave={handleNewCustomerSaved}
              customer={null}
              vorbelegung={vorschlag?.neu}
            />
          )}
        </>
      ) : null}
    </div>
  );
};
